/*
 * Copyright (c) 2021 by Leyton Group - All Rights Reserved.
 *  This source code, and resulting software, is the confidential and proprietary information
 *  ("Proprietary Information") and is the intellectual property ("Intellectual Property")
 *  of Leyton Group, Inc. ("The Company"). You shall not disclose such Proprietary Information
 *  and shall use it only in accordance with the terms and conditions of any and all license
 *  agreements you have entered into with The Company.
 */

import Vue from "vue";
import App from "./App.vue";
import { router } from "./plugins/vue-router";
import "./plugins/components/vue-perfect-scrollbar";
import "./plugins/components/vue-multi-select";
import "./plugins/components/vue-upload-component";
import "./plugins/components/vue-datepicker";
import "./plugins/components/vue-owl-carousel";
import "./directives/select-overflow";
import "./directives/tabs-scroll";
import store from "./store";
import { BootstrapVue, IconsPlugin } from "bootstrap-vue";
import Axios from "axios";
import axios from "./interceptor";
import VueI18n from "vue-i18n";
import { cookieUtils } from "./shared/cookie.util";
import VueNotification from "@kugatsu/vuenotification";
import * as d3 from "d3";
const cookieConsentConfig = {
  guiOptions: {
    consentModal: {
      layout: "box wide",
      position: "bottom right"
    },
    preferencesModal: {
      layout: "box"
    }
  },

  onFirstConsent: ({ cookie }) => {
    if (cookie.categories.includes("analytics")) {
      initializeMatomo();
    }
  },

  onConsent: ({ cookie }) => {
    if (cookie.categories.includes("analytics")) {
      initializeMatomo();
    }
  },

  onChange: (cookiePreferences, changedCookies) => {
    if (changedCookies.includes("analytics")) {
      if (cookiePreferences.includes("analytics")) {
        initializeMatomo();
      }
    }
  },

  categories: {
    necessary: {
      readOnly: true,
      enabled: true
    },
    analytics: {
      autoClear: {
        cookies: [
          {
            name: /^(_pk_id\.|_pk_ref\.|_pk_ses\.)/
          }
        ]
      }
    }
  },

  language: {
    default: "fr",

    translations: {
      fr: {
        consentModal: {
          title: "Autoriser les cookies!",
          description:
            "Ce site utilise des cookies pour vous garantir la meilleure expérience sur notre site.",
          acceptAllBtn: "Accepter",
          acceptNecessaryBtn: "Refuser",
          showPreferencesBtn: "Gérer les préférences",
          closeIconLabel: "Rejeter tout et fermer",
          footer: `
            <a href="/documents/legal_notices_GWT.pdf" target="_blank" rel="noopener noreferrer">
              Politique de confidentialité</a>          `
        },
        preferencesModal: {
          title: "Préférences des cookies",
          acceptAllBtn: "Accepter tout",
          acceptNecessaryBtn: "Refuser tout",
          savePreferencesBtn: "Enregistrer les préférences",
          sections: [
            {
              title: "Utilisation des cookies",
              description:
                'Nous utilisons des cookies pour optimiser votre expérience sur notre site. Certains sont essentiels au bon fonctionnement de nos services, tandis que d\'autres nous aident à mieux comprendre et améliorer votre interaction avec nos contenus. Vous avez la possibilité d\'accepter ou de refuser les cookies selon vos préférences. Pour en savoir plus, veuillez consulter notre <a href="/documents/legal_notices_GWT.pdf" target="_blank" rel="noopener noreferrer" class="cc__link">politique de confidentialité.</a>.'
            },
            {
              title: "Cookies strictement nécessaires",
              description:
                "Ces cookies sont essentiels au fonctionnement du site web et ne peuvent pas être désactivés dans nos systèmes. Ils sont généralement configurés pour répondre à des actions que vous effectuez et qui correspondent à une demande de service, comme définir vos préférences de confidentialité, vous connecter ou remplir des formulaires.",
              linkedCategory: "necessary"
            },
            {
              title: "Cookies de performance et d’analyse",
              description:
                "Ces cookies nous permettent de mesurer et d'améliorer les performances de notre site web. Ils collectent des données anonymes sur la manière dont les visiteurs interagissent avec le site, comme les pages les plus consultées, le temps passé ou les erreurs rencontrées.",
              linkedCategory: "analytics"
            },
            {
              title: "Plus d'informations",
              description:
                'Pour toute question concernant notre politique sur les cookies et vos choix, veuillez <a class="cc__link" href="mailto:gigawhat@leyton.com">nous contacter</a>.'
            }
          ]
        }
      }
    }
  }
};

Vue.use(VueNotification, {
  timer: 3,
  showCloseIcn: true,
  showLeftIcn: true,
  dark: {
    background: "#2F3C5F",
    color: "#E4E4E4"
  },
  success: {
    background: "#7AB145",
    color: "#ffffff"
  },
  error: {
    background: "#B00002",
    color: "#ffffff"
  },
  warning: {
    background: "#FFFCF0",
    color: "#000000"
  },
  primary: {
    background: "#00588B",
    color: "#ffffff"
  },
  position: "topCenter"
});

import UUID from "vue-uuid";

Vue.use(UUID);

// Import Bootstrap an BootstrapVue CSS files (order is important)
import "bootstrap-vue/dist/bootstrap-vue";
import Vuelidate from "vuelidate";

// Make BootstrapVue available throughout your project
Vue.use(BootstrapVue);
// Optionally install the BootstrapVue icon components plugin
Vue.use(IconsPlugin);
Vue.use(Vuelidate);
Vue.use(Axios, axios);
axios.defaults.showLoader = true;
import moment from "moment";
Vue.prototype.moment = moment;
Vue.use(VueI18n);
Vue.use(CookieConsent, cookieConsentConfig);

Vue.filter("formatNumber", function(value) {
  return new Intl.NumberFormat("fr-FR").format(value);
});

Vue.filter("address", function(address) {
  if (!address) return "";

  const addressNumber = address.addressNumber || "";
  const addressRoad = address.addressRoad || "";
  const city = address.city || "";
  const postalCode = address.postalCode || "";

  return `${addressNumber} ${addressRoad} ${city} ${postalCode}`.trim();
});

Vue.filter("importStatusBadge", function(status) {
  switch (status) {
    case "PENDING":
      return { badgeText: "En attente", s: "grey" };
    case "IN_PROGRESS":
      return { badgeText: "En cours", s: "blue" };
    case "DONE":
      return { badgeText: "Terminé", s: "green" };
    case "PARTIALLY_DONE":
      return { badgeText: "Quelques rejets", s: "yellow" };
    case "FAILED":
      return { badgeText: "Echoué", s: "red" };
    default:
      return { badgeText: "Unknown", s: "grey" };
  }
});

Vue.filter("importStatusText", function(status) {
  switch (status) {
    case "PENDING":
      return "En attente";
    case "IN_PROGRESS":
      return "En cours";
    case "DONE":
      return "Terminé";
    case "PARTIALLY_DONE":
      return "Quelques rejets";
    case "FAILED":
      return "Echoué";
    default:
      return "Unknown";
  }
});

Vue.filter("importStatusStyle", function(status) {
  switch (status) {
    case "PENDING":
      return "grey";
    case "IN_PROGRESS":
      return "blue";
    case "DONE":
      return "green";
    case "PARTIALLY_DONE":
      return "yellow";
    case "FAILED":
      return "red";
    default:
      return "grey";
  }
});

/* ======================
 * Global Components
 * ======================
 */
import "@/components";
import "@/layouts";
import i18n from "./i18n";
import { publicService } from "./services";
import { matomoInitializer } from "./plugins/matomo";
import VueTour from "vue-tour";
import VueLogger from "vuejs-logger";
import CookieConsent from "@/plugins/CookieConsentVue";

require("vue-tour/dist/vue-tour.css");

Vue.use(VueTour);

const logOptions = {
  isEnabled: true,
  logLevel: process.env.NODE_ENV === "production" ? "error" : "debug",
  stringifyArguments: false,
  showLogLevel: true,
  showMethodName: true,
  separator: "|",
  showConsoleColors: true
};
Vue.use(VueLogger, logOptions);

Vue.config.productionTip = false;

const checkRefreshFrequency = 3600000;
setInterval(() => {
  if (!cookieUtils.doesJWTCookieExist("Refresh")) {
    store.commit("account/logout");
    router.push("/login", Function);
  }
}, checkRefreshFrequency);

function closeDropdownOnScroll(el, selectIsOpen, dropdown, vnode, originals) {
  if (selectIsOpen) {
    el.style.position = originals.originalPosition;
    el.style.width = originals.originalWidth;
    el.style.zIndex = originals.originalZIndex;
    el.style.transform = "translateY(0)";
    dropdown.style.top = originals.originalTopDropdown;
    dropdown.style.transform = `translateY(0)`;
    el.classList.remove("multiselect--active");
    dropdown.style.display = "none";
    // el.querySelector('input').blur();
    vnode.child.isOpen = false;
  }
}

function wheelScroll(
  el,
  selectIsOpen,
  vnode,
  dropdown,
  psContainer,
  modalScroll,
  dropdownScrolled
) {
  let localeDropdownScrolled = dropdownScrolled;
  "wheel scroll".split(" ").forEach(e => {
    dropdown.addEventListener(
      e,
      () => {
        dropdownScrolled = true;
      },
      false
    );

    if (psContainer) {
      psContainer.addEventListener(
        "scroll",
        () => {
          if (!localeDropdownScrolled) {
            closeDropdownOnScroll(el, selectIsOpen, dropdown, vnode);
          }
          localeDropdownScrolled = false;
          dropdownScrolled = localeDropdownScrolled;
        },
        false
      );
    }
    if (modalScroll) {
      modalScroll.addEventListener(
        e,
        () => {
          if (!localeDropdownScrolled) {
            closeDropdownOnScroll(el, selectIsOpen, dropdown, vnode);
          }
          localeDropdownScrolled = false;
          dropdownScrolled = localeDropdownScrolled;
        },
        false
      );
    }
  });
}

Vue.directive("select-overflow", {
  inserted: (el, _binding, vnode) => {
    let originalWidth;
    let originalPosition;
    let originalZIndex;
    let selectIsOpen = false;
    let dropdown;
    let originalTopDropdown;
    const psContainer = el.closest(".ps-container");
    const modalScroll = el.closest(".modal-scroll");
    const windowInnerHeight = window.innerHeight;
    const dropdownScrolled = false;

    for (let i = 0; i < el.childNodes.length; i++) {
      if (
        el.childNodes[i].className &&
        el.childNodes[i].className.includes("multiselect__content-wrapper")
      ) {
        dropdown = el.childNodes[i];
        break;
      }
    }

    vnode.child.$watch("isOpen", isOpen => {
      selectIsOpen = isOpen;
      if (isOpen) {
        const { offsetWidth } = el;
        const viewportOffset = el.getBoundingClientRect();
        const top = viewportOffset.top;
        originalWidth = el.style.width;
        originalPosition = el.style.position;
        originalZIndex = el.style.zIndex;
        el.style.width = `${offsetWidth}px`;
        el.style.position = "fixed";
        el.style.transform = `translateY(${-psContainer.scrollTop -
          modalScroll.scrollTop}px)`;
        el.style.zIndex = 2;
        const _maxHeight = 150;
        if (windowInnerHeight - top < _maxHeight) {
          originalTopDropdown = dropdown.style.top;
          dropdown.style.maxHeight = "150px";
          //  dropdown.style.top = 'calc(-100%)';
          dropdown.style.transform = `translateY(calc(-100% - 48px))`;
        }
      } else {
        el.style.position = originalPosition;
        el.style.width = originalWidth;
        el.style.zIndex = originalZIndex;
        el.style.transform = "translateY(0)";
        dropdown.style.top = originalTopDropdown;
        dropdown.style.transform = `translateY(0)`;
      }
    });
    const originals = {
      originalPosition,
      originalWidth,
      originalZIndex,
      originalTopDropdown
    };
    closeDropdownOnScroll(el, selectIsOpen, dropdown, vnode, originals);
    wheelScroll(
      el,
      selectIsOpen,
      vnode,
      dropdown,
      psContainer,
      modalScroll,
      dropdownScrolled
    );
  }
});

export function initializeMatomo() {
  publicService
    .getAppInfo()
    .then(response => {
      console.log("matomo initialized");
      matomoInitializer.initMatomo(response.matomoUrl, response.matomoSiteId);
    })
    .catch(() => {});
}

new Vue({
  router,
  store,
  i18n,
  d3,
  render: h => h(App),
  created() {
    const cookieConsentInstance = this.$CookieConsent;
    cookieConsentInstance.onReady(() => {
      const consentedCookies = cookieConsentInstance.getConsentedCookies();
      if (consentedCookies.includes("analytics")) {
        console.log("initialize matomo");
        initializeMatomo();
      }
    });
  }
}).$mount("#app");
